import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import DashboardHeader from "@/components/DashboardHeader";
import MobileBottomNav from "@/components/MobileBottomNav";
import FloatingHelpAvatar from "@/components/FloatingHelpAvatar";
import CourseLoadingScreen from "@/components/CourseLoadingScreen";
import ConfirmModal from "@/components/ConfirmModal";
import { 
  BookOpen, Plus, X, Loader2, AlertCircle, Check, Edit2, 
  Users, Search, ChevronRight, MoreVertical, User, Filter,
  GraduationCap, Layers, Calendar, Briefcase, Trash2,
  ChevronDown, UserCheck, BookMarked, Unlink, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ══════════════════════════════════════════════════════════════════════════════
// FILTER BAR COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
function FilterBar({ filters, setFilters, levels, grades, sections, subjects, teachers, academicYears, onClear }) {
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter grades by selected level
  const filteredGrades = filters.level_id 
    ? grades.filter(g => g.nivel_id === filters.level_id)
    : grades;
  
  const activeFiltersCount = Object.values(filters).filter(v => v).length;
  
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors"
        >
          <Filter className="w-5 h-5" />
          <span className="font-medium">Filtros</span>
          {activeFiltersCount > 0 && (
            <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
              {activeFiltersCount}
            </span>
          )}
          <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>
        
        {activeFiltersCount > 0 && (
          <button
            onClick={onClear}
            className="text-sm text-gray-500 hover:text-red-500 transition-colors"
          >
            Limpiar filtros
          </button>
        )}
      </div>
      
      {showFilters && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 pt-4 border-t border-gray-100">
          {/* Level Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Nivel</label>
            <select
              value={filters.level_id || ""}
              onChange={(e) => setFilters({...filters, level_id: e.target.value, grade_id: ""})}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos</option>
              {levels.map(l => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
          </div>
          
          {/* Grade Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Grado</label>
            <select
              value={filters.grade_id || ""}
              onChange={(e) => setFilters({...filters, grade_id: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!filters.level_id}
            >
              <option value="">Todos</option>
              {filteredGrades.map(g => (
                <option key={g.id} value={g.id}>{g.nombre}</option>
              ))}
            </select>
          </div>
          
          {/* Section Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Sección</label>
            <select
              value={filters.section_id || ""}
              onChange={(e) => setFilters({...filters, section_id: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todas</option>
              {sections.map(s => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          </div>
          
          {/* Subject Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Asignatura</label>
            <select
              value={filters.subject_id || ""}
              onChange={(e) => setFilters({...filters, subject_id: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todas</option>
              {subjects.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          
          {/* Teacher Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Profesor</label>
            <select
              value={filters.teacher_id || ""}
              onChange={(e) => setFilters({...filters, teacher_id: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos</option>
              {teachers.map(t => (
                <option key={t.id} value={t.id}>{t.name} {t.last_name}</option>
              ))}
            </select>
          </div>
          
          {/* Academic Year Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Año Académico</label>
            <select
              value={filters.academic_year_id || ""}
              onChange={(e) => setFilters({...filters, academic_year_id: e.target.value || ""})}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos</option>
              {academicYears.map(y => (
                <option key={y.id} value={y.id}>{y.year} {y.status === "activo" ? "✓" : ""}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TEACHER SEARCH (autocomplete) — search by teacher to see all their assignments
// ══════════════════════════════════════════════════════════════════════════════
function TeacherSearchBar({ teachers, selectedId, onSelect, onClear, resultCount }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const fullName = (t) => `${t.name || ""} ${t.last_name || ""}`.trim() || t.name || "";
  const selected = selectedId ? teachers.find(t => t.id === selectedId) : null;
  const q = query.trim().toLowerCase();
  const results = (q
    ? teachers.filter(t => fullName(t).toLowerCase().includes(q) || (t.email || "").toLowerCase().includes(q))
    : teachers
  ).slice(0, 30);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4 relative z-30" data-testid="teacher-search-bar">
      <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
        Buscar por docente
      </label>
      {selected ? (
        <div className="flex items-center gap-3 p-2.5 bg-blue-50 border border-blue-200 rounded-xl" data-testid="teacher-search-selected">
          <div className="w-9 h-9 rounded-lg overflow-hidden bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shrink-0">
            {selected.photo_url ? (
              <img src={selected.photo_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-bold text-sm">{fullName(selected).charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{fullName(selected)}</p>
            <p className="text-xs text-blue-600">
              {resultCount} curso{resultCount !== 1 ? "s" : ""} / asignatura{resultCount !== 1 ? "s" : ""} asignada{resultCount !== 1 ? "s" : ""}
            </p>
          </div>
          <button
            onClick={() => { setQuery(""); onClear(); }}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-white rounded-lg transition-colors"
            data-testid="teacher-search-clear"
            aria-label="Quitar filtro de docente"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Escribe el nombre del docente..."
            data-testid="teacher-search-input"
            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          {open && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
              <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-2xl">
                {results.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-400">No se encontraron docentes</div>
                ) : (
                  results.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => { onSelect(t.id); setQuery(""); setOpen(false); }}
                      data-testid={`teacher-search-option-${t.id}`}
                      className="w-full px-4 py-2.5 flex items-center gap-3 text-left hover:bg-blue-50 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg overflow-hidden bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shrink-0">
                        {t.photo_url ? (
                          <img src={t.photo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-white font-bold text-xs">{fullName(t).charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{fullName(t)}</p>
                        {t.email && <p className="text-xs text-gray-400 truncate">{t.email}</p>}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// ASSIGNMENT CARD COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
function AssignmentCard({ assignment, onEdit, onDelete, selected, onToggleSelect }) {
  const [showMenu, setShowMenu] = useState(false);
  
  return (
    <div 
      className={`bg-white rounded-xl border p-4 hover:shadow-lg transition-all duration-300 group ${
        selected ? "border-blue-500 ring-2 ring-blue-200 shadow-md" : "border-gray-100 hover:border-blue-200"
      }`}
      data-testid={`assignment-card-${assignment.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {/* Selection checkbox */}
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect(assignment.id)}
            onClick={(e) => e.stopPropagation()}
            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-400 cursor-pointer shrink-0"
            data-testid={`assignment-select-${assignment.id}`}
            aria-label="Seleccionar asignación"
          />
          {/* Subject Color Indicator */}
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold shadow-md"
            style={{ backgroundColor: assignment.subject_color || '#3B82F6' }}
          >
            <BookOpen className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              {assignment.subject_name || <span className="text-red-500">Sin curso vinculado</span>}
            </h3>
            <p className="text-xs text-gray-500">{assignment.subject_code}</p>
          </div>
        </div>
        
        {/* Actions Menu */}
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
            data-testid={`assignment-menu-${assignment.id}`}
          >
            <MoreVertical className="w-4 h-4 text-gray-400" />
          </button>
          
          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-20 min-w-[140px]">
                <button
                  onClick={() => { onEdit(assignment); setShowMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Editar
                </button>
                <button
                  onClick={() => { onDelete(assignment); setShowMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Eliminar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Teacher Info */}
      <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
        {assignment.teacher_photo ? (
          <img src={assignment.teacher_photo} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-xs font-bold">
            {assignment.teacher_name?.charAt(0) || "P"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{assignment.teacher_name}</p>
          <p className="text-xs text-gray-500 capitalize">{assignment.role}</p>
        </div>
      </div>
      
      {/* Context Info */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center gap-1.5 text-gray-600">
          <GraduationCap className="w-3.5 h-3.5 text-blue-500" />
          <span className="truncate">{assignment.level_name}</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-600">
          <Layers className="w-3.5 h-3.5 text-purple-500" />
          <span className="truncate">{assignment.grade_name}</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-600">
          <Users className="w-3.5 h-3.5 text-emerald-500" />
          <span>Sección {assignment.section_name}</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-600">
          <Calendar className="w-3.5 h-3.5 text-amber-500" />
          <span>{assignment.academic_year || assignment.school_year}</span>
        </div>
      </div>
      
      {/* Status Badge */}
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
          assignment.status === 'activo' 
            ? 'bg-emerald-100 text-emerald-700' 
            : 'bg-gray-100 text-gray-600'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${assignment.status === 'activo' ? 'bg-emerald-500' : 'bg-gray-400'}`} />
          {assignment.status === 'activo' ? 'Activo' : 'Inactivo'}
        </span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TEACHERS SUMMARY SIDEBAR
// ══════════════════════════════════════════════════════════════════════════════
function TeachersSummary({ teachers, onSelect, selectedTeacherId }) {
  if (!teachers.length) return null;
  
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Briefcase className="w-5 h-5 text-blue-500" />
        Carga Docente
      </h3>
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {teachers.map(teacher => (
          <button
            key={teacher.id}
            onClick={() => onSelect(teacher.id === selectedTeacherId ? null : teacher.id)}
            className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all ${
              teacher.id === selectedTeacherId 
                ? 'bg-blue-50 border border-blue-200' 
                : 'hover:bg-gray-50 border border-transparent'
            }`}
          >
            {teacher.photo_url ? (
              <img src={teacher.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-xs font-bold">
                {teacher.name?.charAt(0) || "P"}
              </div>
            )}
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{teacher.name}</p>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
              teacher.assignments_count > 0 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-gray-100 text-gray-500'
            }`}>
              {teacher.assignments_count}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CREATE/EDIT ASSIGNMENT MODAL
// ══════════════════════════════════════════════════════════════════════════════
function AssignmentModal({ isOpen, onClose, token, assignment, onSuccess, academicData, existingAssignments = [] }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [teacherSearch, setTeacherSearch] = useState("");
  const [teacherOpen, setTeacherOpen] = useState(false);
  const [subjectSearch, setSubjectSearch] = useState("");
  const [subjectOpen, setSubjectOpen] = useState(false);
  const teacherRef = useRef(null);
  const subjectRef = useRef(null);
  const [form, setForm] = useState({
    level_id: "",
    grade_id: "",
    section_id: "",
    subject_id: "",
    teacher_id: "",
    academic_year_id: "",
    role: "titular",
    status: "activo"
  });
  
  const isEdit = !!assignment;
  const headers = { Authorization: `Bearer ${token}` };
  
  // Cascade filters - STRICT HIERARCHY
  // Grades filtered by Level
  const filteredGrades = form.level_id 
    ? academicData.grades.filter(g => g.nivel_id === form.level_id)
    : [];
  
  // Sections filtered by Grade (FIXED)
  const filteredSections = form.grade_id 
    ? academicData.sections.filter(s => s.grado_id === form.grade_id)
    : [];
  
  // Subjects filtered by Level + Grade + Section AND excluding already assigned subjects in this section
  const filteredSubjects = (form.level_id && form.grade_id)
    ? academicData.subjects.filter(s => {
        // Must match level and grade
        if (s.level_id !== form.level_id || s.grade_id !== form.grade_id) return false;

        // If a section is selected and the subject is tagged to a different section,
        // exclude it UNLESS there's an existing assignment record for this section
        // (this covers subjects shared across sections that already have a record here).
        if (form.section_id && s.section_id && s.section_id !== form.section_id) {
          const hasRecordInThisSection = existingAssignments.some(a =>
            a.section_id === form.section_id &&
            a.subject_id === s.id
          );
          if (!hasRecordInThisSection) return false;
        }

        // If section is selected, check if this subject is already assigned in this section
        if (form.section_id) {
          const alreadyAssigned = existingAssignments.some(a => 
            a.level_id === form.level_id &&
            a.grade_id === form.grade_id &&
            a.section_id === form.section_id &&
            a.subject_id === s.id &&
            // If editing, don't exclude the current assignment's subject
            (!isEdit || a.id !== assignment?.id)
          );
          if (alreadyAssigned) return false;
        }
        
        return true;
      })
    : [];
  
  // Currently-selected subject resolved from the FULL subjects list (and, as a
  // last resort, from the enriched assignment record). The cascade filter above
  // can legitimately exclude the selected subject when editing a bulk-created
  // assignment whose stored level/grade differs slightly from the subject doc —
  // that used to render "()" with no name. This guarantees the label always
  // resolves and the subject stays selectable.
  const selectedSubjectFull = form.subject_id
    ? (academicData.subjects.find(s => s.id === form.subject_id)
        || (assignment && assignment.subject_id === form.subject_id
              ? {
                  id: form.subject_id,
                  name: assignment.subject_name || "",
                  code: assignment.subject_code || "",
                  color: assignment.subject_color,
                }
              : null))
    : null;
  const subjectOptions = (selectedSubjectFull && !filteredSubjects.some(s => s.id === selectedSubjectFull.id))
    ? [selectedSubjectFull, ...filteredSubjects]
    : filteredSubjects;
  
  // Sort academic years (active first, then by year descending)
  const sortedYears = [...(academicData.academicYears || [])].sort((a, b) => {
    if (a.status === "activo" && b.status !== "activo") return -1;
    if (b.status === "activo" && a.status !== "activo") return 1;
    return b.year - a.year;
  });
  
  useEffect(() => {
    if (isOpen) {
      if (assignment) {
        setForm({
          level_id: assignment.level_id || "",
          grade_id: assignment.grade_id || "",
          section_id: assignment.section_id || "",
          subject_id: assignment.subject_id || "",
          teacher_id: assignment.teacher_id || "",
          academic_year_id: assignment.academic_year_id || "",
          role: assignment.role || "titular",
          status: assignment.status || "activo"
        });
      } else {
        // Find the active year as default
        const activeYear = academicData.academicYears?.find(y => y.status === "activo");
        const defaultYearId = activeYear?.id || sortedYears[0]?.id || "";
        
        setForm({
          level_id: "",
          grade_id: "",
          section_id: "",
          subject_id: "",
          teacher_id: "",
          academic_year_id: defaultYearId,
          role: "titular",
          status: "activo"
        });
      }
      setError("");
      setTeacherSearch("");
      setSubjectSearch("");
      setTeacherOpen(false);
      setSubjectOpen(false);
    }
  }, [isOpen, assignment, academicData.academicYears]);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (teacherRef.current && !teacherRef.current.contains(e.target)) setTeacherOpen(false);
      if (subjectRef.current && !subjectRef.current.contains(e.target)) setSubjectOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // Handler for Level change - reset dependent fields
  const handleLevelChange = (e) => {
    setForm({
      ...form,
      level_id: e.target.value,
      grade_id: "",
      section_id: "",
      subject_id: ""
    });
  };
  
  // Handler for Grade change - reset dependent fields
  const handleGradeChange = (e) => {
    setForm({
      ...form,
      grade_id: e.target.value,
      section_id: "",
      subject_id: ""
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.level_id || !form.grade_id || !form.section_id || !form.subject_id || !form.teacher_id) {
      setError("Todos los campos son obligatorios");
      return;
    }
    
    if (!form.academic_year_id) {
      setError("Selecciona un año académico");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      // Get the year to extract school_year for backward compatibility
      const selectedYear = academicData.academicYears?.find(y => y.id === form.academic_year_id);
      const schoolYear = selectedYear?.year || new Date().getFullYear();
      
      const submitData = {
        ...form,
        school_year: schoolYear // Keep school_year for backward compatibility
      };
      
      if (isEdit) {
        await axios.put(`${API}/academic/assignments/${assignment.id}`, submitData, { headers });
      } else {
        await axios.post(`${API}/academic/assignments`, submitData, { headers });
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al guardar la asignación");
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-[200] overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4 py-8">
        <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" data-testid="assignment-modal">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <UserCheck className="w-8 h-8 text-white" />
              <div className="text-white">
                <h2 className="text-xl font-bold">{isEdit ? "Editar" : "Nueva"} Asignación</h2>
                <p className="text-sm text-white/80">Asignar profesor a asignatura</p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
            
            {/* Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <GraduationCap className="w-4 h-4 inline mr-1 text-blue-500" />
                Nivel Educativo *
              </label>
              <select
                value={form.level_id}
                onChange={handleLevelChange}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                required
              >
                <option value="">Seleccionar nivel...</option>
                {academicData.levels.map(l => (
                  <option key={l.id} value={l.id}>{l.nombre}</option>
                ))}
              </select>
            </div>
            
            {/* Grade */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Layers className="w-4 h-4 inline mr-1 text-purple-500" />
                Grado *
              </label>
              <select
                value={form.grade_id}
                onChange={handleGradeChange}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-50 disabled:text-gray-400"
                required
                disabled={!form.level_id}
              >
                <option value="">Seleccionar grado...</option>
                {filteredGrades.map(g => (
                  <option key={g.id} value={g.id}>{g.nombre}</option>
                ))}
              </select>
              {!form.level_id && (
                <p className="text-xs text-gray-400 mt-1">Primero selecciona un nivel</p>
              )}
            </div>
            
            {/* Section - FILTERED BY GRADE */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Users className="w-4 h-4 inline mr-1 text-emerald-500" />
                Sección *
              </label>
              <select
                value={form.section_id}
                onChange={(e) => setForm({...form, section_id: e.target.value})}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-50 disabled:text-gray-400"
                required
                disabled={!form.grade_id}
              >
                <option value="">Seleccionar sección...</option>
                {filteredSections.map(s => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
              {!form.grade_id && (
                <p className="text-xs text-gray-400 mt-1">Primero selecciona un grado</p>
              )}
              {form.grade_id && filteredSections.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No hay secciones registradas para este grado</p>
              )}
            </div>
            
            {/* Subject - AUTOCOMPLETE */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <BookOpen className="w-4 h-4 inline mr-1 text-amber-500" />
                Asignatura *
              </label>
              {!form.grade_id ? (
                <div className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-gray-400 text-sm">
                  Primero selecciona un grado
                </div>
              ) : (
                <div className="relative" ref={subjectRef}>
                  {form.subject_id && !subjectOpen ? (
                    (() => {
                      const sel = selectedSubjectFull || filteredSubjects.find(s => s.id === form.subject_id);
                      return (
                        <div
                          className="w-full flex items-center gap-3 px-4 py-2 border border-gray-200 rounded-xl cursor-pointer hover:border-blue-300 transition-colors"
                          onClick={() => { setSubjectOpen(true); setSubjectSearch(""); }}
                          data-testid="subject-selected-preview"
                        >
                          {sel?.image_url ? (
                            <img src={sel.image_url} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: (sel?.color || '#F59E0B') + '20' }}>
                              <BookOpen className="w-4 h-4" style={{ color: sel?.color || '#F59E0B' }} />
                            </div>
                          )}
                          <span className="text-sm font-medium text-gray-800 truncate">{sel?.name} ({sel?.code})</span>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setForm({...form, subject_id: ""}); setSubjectSearch(""); }} className="ml-auto text-gray-400 hover:text-gray-600 flex-shrink-0">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })()
                  ) : (
                    <input
                      type="text"
                      placeholder="Buscar asignatura..."
                      value={subjectSearch}
                      onChange={(e) => { setSubjectSearch(e.target.value); setSubjectOpen(true); }}
                      onFocus={() => { setSubjectOpen(true); setSubjectSearch(""); }}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      data-testid="subject-autocomplete-input"
                    />
                  )}
                  {subjectOpen && (
                    <div className="absolute z-[200] top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                      {subjectOptions
                        .filter(s => {
                          const q = subjectSearch.toLowerCase();
                          return !q || s.name.toLowerCase().includes(q) || (s.code || "").toLowerCase().includes(q);
                        })
                        .map(s => (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => { setForm({...form, subject_id: s.id}); setSubjectOpen(false); setSubjectSearch(""); }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors text-left ${form.subject_id === s.id ? "bg-blue-50" : ""}`}
                            data-testid={`subject-option-${s.id}`}
                          >
                            {s.image_url ? (
                              <img src={s.image_url} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: (s.color || '#F59E0B') + '20' }}>
                                <BookOpen className="w-4 h-4" style={{ color: s.color || '#F59E0B' }} />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{s.name}</p>
                              <p className="text-xs text-gray-500">{s.code}</p>
                            </div>
                            {form.subject_id === s.id && <Check className="w-4 h-4 text-blue-500 ml-auto flex-shrink-0" />}
                          </button>
                        ))
                      }
                      {subjectOptions.filter(s => {
                        const q = subjectSearch.toLowerCase();
                        return !q || s.name.toLowerCase().includes(q) || (s.code || "").toLowerCase().includes(q);
                      }).length === 0 && (
                        <div className="px-4 py-3 text-sm text-gray-400 text-center">Sin resultados</div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {form.grade_id && form.section_id && filteredSubjects.length === 0 && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Todas las asignaturas de esta sección ya tienen docente asignado
                </p>
              )}
            </div>
            
            {/* Teacher - AUTOCOMPLETE WITH PHOTOS */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User className="w-4 h-4 inline mr-1 text-indigo-500" />
                Profesor *
              </label>
              {academicData.teachers.length === 0 ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm">
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  No hay profesores registrados con rol activo.
                </div>
              ) : (
                <div className="relative" ref={teacherRef}>
                  {/* Selected teacher preview or search input */}
                  {form.teacher_id && !teacherOpen ? (
                    <div
                      className="w-full flex items-center gap-3 px-4 py-2 border border-gray-200 rounded-xl cursor-pointer hover:border-blue-300 transition-colors"
                      onClick={() => { setTeacherOpen(true); setTeacherSearch(""); }}
                      data-testid="teacher-selected-preview"
                    >
                      {(() => {
                        const t = academicData.teachers.find(t => t.id === form.teacher_id);
                        if (!t) return <span className="text-gray-400 text-sm">Seleccionar profesor...</span>;
                        return (
                          <>
                            {t.photo_url ? (
                              <img src={t.photo_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-indigo-600" />
                              </div>
                            )}
                            <span className="text-sm font-medium text-gray-800 truncate">{t.name} {t.last_name}</span>
                            <button type="button" onClick={(e) => { e.stopPropagation(); setForm({...form, teacher_id: ""}); setTeacherSearch(""); }} className="ml-auto text-gray-400 hover:text-gray-600 flex-shrink-0">
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <input
                      type="text"
                      placeholder="Buscar profesor por nombre..."
                      value={teacherSearch}
                      onChange={(e) => { setTeacherSearch(e.target.value); setTeacherOpen(true); }}
                      onFocus={() => setTeacherOpen(true)}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      data-testid="teacher-autocomplete-input"
                      autoComplete="off"
                    />
                  )}
                  {teacherOpen && (
                    <div className="absolute z-[200] top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-56 overflow-y-auto">
                      {academicData.teachers
                        .filter(t => {
                          const q = teacherSearch.toLowerCase();
                          const fullName = `${t.name} ${t.last_name}`.toLowerCase();
                          return !q || fullName.includes(q);
                        })
                        .map(t => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => { setForm({...form, teacher_id: t.id}); setTeacherOpen(false); setTeacherSearch(""); }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors text-left ${form.teacher_id === t.id ? "bg-blue-50" : ""}`}
                            data-testid={`teacher-option-${t.id}`}
                          >
                            {t.photo_url ? (
                              <img src={t.photo_url} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-indigo-600" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{t.name} {t.last_name}</p>
                              <p className="text-xs text-gray-500">{t.email}</p>
                            </div>
                            {form.teacher_id === t.id && <Check className="w-4 h-4 text-blue-500 ml-auto flex-shrink-0" />}
                          </button>
                        ))
                      }
                      {academicData.teachers.filter(t => {
                        const q = teacherSearch.toLowerCase();
                        return !q || `${t.name} ${t.last_name}`.toLowerCase().includes(q);
                      }).length === 0 && (
                        <div className="px-4 py-3 text-sm text-gray-400 text-center">Sin resultados para "{teacherSearch}"</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Role & Year Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Briefcase className="w-4 h-4 inline mr-1 text-rose-500" />
                  Rol
                </label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({...form, role: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="titular">Titular</option>
                  <option value="auxiliar">Auxiliar</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1 text-cyan-500" />
                  Año Académico
                </label>
                <select
                  value={form.academic_year_id}
                  onChange={(e) => setForm({...form, academic_year_id: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  required
                >
                  <option value="">Seleccionar año...</option>
                  {sortedYears.map(y => (
                    <option key={y.id} value={y.id}>
                      {y.year} {y.status === "activo" ? "(Activo)" : y.status === "futuro" ? "(Futuro)" : "(Cerrado)"}
                    </option>
                  ))}
                </select>
                {sortedYears.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No hay años académicos configurados. Créalos en Años Académicos.
                  </p>
                )}
              </div>
            </div>
            
            {/* Status (only for edit) */}
            {isEdit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({...form, status: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </select>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading || academicData.teachers.length === 0}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="assignment-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {isEdit ? "Guardar Cambios" : "Asignar Profesor"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// BULK ASSIGNMENT MODAL
// ══════════════════════════════════════════════════════════════════════════════
function BulkAssignmentModal({ isOpen, onClose, token, onSuccess, academicData }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1); // 1 = form, 2 = preview
  const [teacherSearch, setTeacherSearch] = useState("");
  const [teacherOpen, setTeacherOpen] = useState(false);
  const teacherRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const [form, setForm] = useState({
    teacher_id: "",
    level_id: "",
    grade_ids: [],
    section_ids: [],
    subject_ids: [],
    academic_year_id: "",
    role: "titular",
    all_sections: false,
  });

  // Derived data
  const filteredGrades = form.level_id
    ? academicData.grades.filter((g) => g.nivel_id === form.level_id)
    : [];

  const filteredSections = form.grade_ids.length > 0
    ? academicData.sections.filter((s) => form.grade_ids.includes(s.grado_id))
    : [];

  const filteredSubjects = form.level_id
    ? academicData.subjects.filter((s) => {
        if (s.level_id !== form.level_id) return false;
        if (form.grade_ids.length > 0 && s.grade_id && !form.grade_ids.includes(s.grade_id)) return false;
        return true;
      })
    : [];

  // Deduplicate subjects by name (same subject across grades/sections)
  const uniqueSubjects = filteredSubjects.reduce((acc, s) => {
    if (!acc.find((x) => x.id === s.id)) acc.push(s);
    return acc;
  }, []);

  const sortedYears = [...(academicData.academicYears || [])].sort((a, b) => {
    if (a.status === "activo" && b.status !== "activo") return -1;
    if (b.status === "activo" && a.status !== "activo") return 1;
    return b.year - a.year;
  });

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      const activeYear = academicData.academicYears?.find((y) => y.status === "activo");
      setForm({
        teacher_id: "",
        level_id: "",
        grade_ids: [],
        section_ids: [],
        subject_ids: [],
        academic_year_id: activeYear?.id || sortedYears[0]?.id || "",
        role: "titular",
        all_sections: false,
      });
      setStep(1);
      setError("");
      setTeacherSearch("");
      setTeacherOpen(false);
    }
  }, [isOpen]);

  // Auto-select all sections when toggled
  useEffect(() => {
    if (form.all_sections) {
      setForm((prev) => ({ ...prev, section_ids: filteredSections.map((s) => s.id) }));
    }
  }, [form.all_sections, form.grade_ids.join(",")]);

  // Close teacher dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (teacherRef.current && !teacherRef.current.contains(e.target)) setTeacherOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Handlers
  const toggleGrade = (gid) => {
    setForm((prev) => {
      const ids = prev.grade_ids.includes(gid)
        ? prev.grade_ids.filter((x) => x !== gid)
        : [...prev.grade_ids, gid];
      return { ...prev, grade_ids: ids, section_ids: [], subject_ids: [], all_sections: false };
    });
  };

  const toggleSection = (sid) => {
    setForm((prev) => {
      const ids = prev.section_ids.includes(sid)
        ? prev.section_ids.filter((x) => x !== sid)
        : [...prev.section_ids, sid];
      return { ...prev, section_ids: ids, all_sections: ids.length === filteredSections.length };
    });
  };

  const toggleSubject = (sid) => {
    setForm((prev) => {
      const ids = prev.subject_ids.includes(sid)
        ? prev.subject_ids.filter((x) => x !== sid)
        : [...prev.subject_ids, sid];
      return { ...prev, subject_ids: ids };
    });
  };

  const selectAllSubjects = () => {
    const allIds = uniqueSubjects.map((s) => s.id);
    setForm((prev) => ({
      ...prev,
      subject_ids: prev.subject_ids.length === allIds.length ? [] : allIds,
    }));
  };

  // Build preview data
  const previewItems = [];
  const gradeMap = Object.fromEntries(academicData.grades.map((g) => [g.id, g.nombre]));
  const sectionMap = Object.fromEntries(academicData.sections.map((s) => [s.id, s]));
  const subjectMap = Object.fromEntries(academicData.subjects.map((s) => [s.id, s]));

  for (const gid of form.grade_ids) {
    for (const sid of form.section_ids) {
      const sec = sectionMap[sid];
      if (!sec || sec.grado_id !== gid) continue;
      for (const subId of form.subject_ids) {
        previewItems.push({
          grade: gradeMap[gid] || gid,
          section: sec.nombre || sid,
          subject: subjectMap[subId]?.name || subId,
        });
      }
    }
  }

  const selectedTeacher = academicData.teachers.find((t) => t.id === form.teacher_id);
  const teacherLabel = selectedTeacher
    ? `${selectedTeacher.name} ${selectedTeacher.last_name || ""}`.trim()
    : "";

  const canPreview =
    form.teacher_id &&
    form.level_id &&
    form.grade_ids.length > 0 &&
    form.section_ids.length > 0 &&
    form.subject_ids.length > 0 &&
    form.academic_year_id;

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      const selectedYear = academicData.academicYears?.find((y) => y.id === form.academic_year_id);
      const res = await axios.post(
        `${API}/academic/assignments/bulk`,
        {
          teacher_id: form.teacher_id,
          level_id: form.level_id,
          grade_ids: form.grade_ids,
          section_ids: form.section_ids,
          subject_ids: form.subject_ids,
          academic_year_id: form.academic_year_id,
          school_year: selectedYear?.year || new Date().getFullYear(),
          role: form.role,
        },
        { headers }
      );
      const { created, skipped } = res.data;
      const parts = [];
      if (created > 0) parts.push(`${created} asignación${created !== 1 ? "es" : ""} creada${created !== 1 ? "s" : ""}`);
      if (skipped > 0) parts.push(`${skipped} ya existia${skipped !== 1 ? "n" : ""}`);
      alert(parts.join(", ") || "Operación completada");
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al crear asignaciones masivas");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[200] overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4 py-8">
        <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl" data-testid="bulk-assignment-modal">
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-500 to-fuchsia-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-white" />
              <div className="text-white">
                <h2 className="text-xl font-bold">Asignación Masiva</h2>
                <p className="text-sm text-white/80">
                  {step === 1 ? "Selecciona profesor, grados, secciones y asignaturas" : `${previewItems.length} asignaciones por crear`}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
              data-testid="bulk-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5">
                {/* Teacher */}
                <div ref={teacherRef} className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Profesor</label>
                  <div
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl cursor-pointer flex items-center justify-between hover:border-violet-300 transition-colors"
                    onClick={() => setTeacherOpen(!teacherOpen)}
                    data-testid="bulk-teacher-select"
                  >
                    <span className={teacherLabel ? "text-gray-900" : "text-gray-400"}>
                      {teacherLabel || "Seleccionar profesor..."}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${teacherOpen ? "rotate-180" : ""}`} />
                  </div>
                  {teacherOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={teacherSearch}
                            onChange={(e) => setTeacherSearch(e.target.value)}
                            placeholder="Buscar profesor..."
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                            autoFocus
                            data-testid="bulk-teacher-search"
                          />
                        </div>
                      </div>
                      {academicData.teachers
                        .filter((t) => {
                          const fullName = `${t.name} ${t.last_name || ""}`.toLowerCase();
                          return fullName.includes(teacherSearch.toLowerCase());
                        })
                        .map((t) => (
                          <button
                            key={t.id}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-violet-50 flex items-center gap-2 ${
                              form.teacher_id === t.id ? "bg-violet-50 text-violet-700 font-medium" : "text-gray-700"
                            }`}
                            onClick={() => {
                              setForm((prev) => ({ ...prev, teacher_id: t.id }));
                              setTeacherOpen(false);
                            }}
                          >
                            <User className="w-4 h-4 flex-shrink-0" />
                            {t.name} {t.last_name || ""}
                            {form.teacher_id === t.id && <Check className="w-4 h-4 ml-auto text-violet-500" />}
                          </button>
                        ))}
                    </div>
                  )}
                </div>

                {/* Level */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nivel</label>
                  <select
                    value={form.level_id}
                    onChange={(e) => setForm((prev) => ({ ...prev, level_id: e.target.value, grade_ids: [], section_ids: [], subject_ids: [], all_sections: false }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    data-testid="bulk-level-select"
                  >
                    <option value="">Seleccionar nivel...</option>
                    {academicData.levels.map((l) => (
                      <option key={l.id} value={l.id}>{l.nombre}</option>
                    ))}
                  </select>
                </div>

                {/* Grades (multi-select checkboxes) */}
                {filteredGrades.length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Grados <span className="text-gray-400 font-normal">({form.grade_ids.length} seleccionados)</span>
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-xl p-3" data-testid="bulk-grades-list">
                      {filteredGrades.map((g) => (
                        <label key={g.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-violet-50 rounded-lg px-2 py-1.5 transition-colors">
                          <input
                            type="checkbox"
                            checked={form.grade_ids.includes(g.id)}
                            onChange={() => toggleGrade(g.id)}
                            className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                          />
                          <span className="text-gray-700">{g.nombre}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sections (multi-select, depends on grades) */}
                {filteredSections.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-sm font-semibold text-gray-700">
                        Secciones <span className="text-gray-400 font-normal">({form.section_ids.length} seleccionadas)</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setForm((prev) => ({
                          ...prev,
                          all_sections: !prev.all_sections,
                          section_ids: !prev.all_sections ? filteredSections.map((s) => s.id) : [],
                        }))}
                        className="text-xs text-violet-600 hover:text-violet-800 font-medium"
                        data-testid="bulk-sections-toggle-all"
                      >
                        {form.all_sections ? "Deseleccionar todas" : "Seleccionar todas"}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-xl p-3" data-testid="bulk-sections-list">
                      {filteredSections.map((s) => {
                        const gradeName = gradeMap[s.grado_id] || "";
                        return (
                          <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-violet-50 rounded-lg px-2 py-1.5 transition-colors">
                            <input
                              type="checkbox"
                              checked={form.section_ids.includes(s.id)}
                              onChange={() => toggleSection(s.id)}
                              className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                            />
                            <span className="text-gray-700">{gradeName} - {s.nombre}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Subjects (multi-select) */}
                {uniqueSubjects.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-sm font-semibold text-gray-700">
                        Asignaturas <span className="text-gray-400 font-normal">({form.subject_ids.length} seleccionadas)</span>
                      </label>
                      <button
                        type="button"
                        onClick={selectAllSubjects}
                        className="text-xs text-violet-600 hover:text-violet-800 font-medium"
                        data-testid="bulk-subjects-toggle-all"
                      >
                        {form.subject_ids.length === uniqueSubjects.length ? "Deseleccionar todas" : "Seleccionar todas"}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-xl p-3" data-testid="bulk-subjects-list">
                      {uniqueSubjects.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-violet-50 rounded-lg px-2 py-1.5 transition-colors">
                          <input
                            type="checkbox"
                            checked={form.subject_ids.includes(s.id)}
                            onChange={() => toggleSubject(s.id)}
                            className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                          />
                          <span className="flex items-center gap-1.5 text-gray-700">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color || "#3B82F6" }} />
                            {s.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Role + Year row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Rol</label>
                    <select
                      value={form.role}
                      onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      data-testid="bulk-role-select"
                    >
                      <option value="titular">Titular</option>
                      <option value="auxiliar">Auxiliar</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Ano Académico</label>
                    <select
                      value={form.academic_year_id}
                      onChange={(e) => setForm((prev) => ({ ...prev, academic_year_id: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      data-testid="bulk-year-select"
                    >
                      <option value="">Seleccionar...</option>
                      {sortedYears.map((y) => (
                        <option key={y.id} value={y.id}>
                          {y.year} {y.status === "activo" ? "(Activo)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Preview */}
            {step === 2 && (
              <div>
                <div className="mb-4 p-4 rounded-xl bg-violet-50 border border-violet-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-violet-600" />
                    <span className="font-semibold text-violet-900">Profesor: {teacherLabel}</span>
                  </div>
                  <p className="text-sm text-violet-700">
                    Se crearan <strong>{previewItems.length}</strong> asignación{previewItems.length !== 1 ? "es" : ""} como <strong>{form.role}</strong>
                  </p>
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-xl" data-testid="bulk-preview-list">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-2 text-gray-600 font-medium">#</th>
                        <th className="text-left px-4 py-2 text-gray-600 font-medium">Grado</th>
                        <th className="text-left px-4 py-2 text-gray-600 font-medium">Sección</th>
                        <th className="text-left px-4 py-2 text-gray-600 font-medium">Asignatura</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewItems.map((item, i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                          <td className="px-4 py-1.5 text-gray-400">{i + 1}</td>
                          <td className="px-4 py-1.5 text-gray-800">{item.grade}</td>
                          <td className="px-4 py-1.5 text-gray-800">{item.section}</td>
                          <td className="px-4 py-1.5 text-gray-800">{item.subject}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between rounded-b-2xl">
            {step === 2 && (
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2.5 text-gray-600 hover:text-gray-900 font-medium transition-colors"
                data-testid="bulk-back-btn"
              >
                Volver
              </button>
            )}
            {step === 1 && <div />}

            {step === 1 ? (
              <button
                disabled={!canPreview}
                onClick={() => setStep(2)}
                className={`px-6 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
                  canPreview
                    ? "bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white shadow-lg shadow-violet-500/25 hover:shadow-xl"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
                data-testid="bulk-preview-btn"
              >
                <ChevronRight className="w-4 h-4" />
                Ver Preview ({previewItems.length})
              </button>
            ) : (
              <button
                disabled={loading || previewItems.length === 0}
                onClick={handleSubmit}
                className="px-6 py-2.5 rounded-xl font-medium bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white shadow-lg shadow-violet-500/25 hover:shadow-xl transition-all flex items-center gap-2 disabled:opacity-50"
                data-testid="bulk-confirm-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {loading ? "Creando..." : `Confirmar ${previewItems.length} Asignaciones`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function TeacherAssignmentsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [teachersSummary, setTeachersSummary] = useState([]);
  
  // Academic data for form
  const [academicData, setAcademicData] = useState({
    levels: [],
    grades: [],
    sections: [],
    subjects: [],
    teachers: [],
    academicYears: []
  });
  
  // Filters
  const [filters, setFilters] = useState({
    level_id: "",
    grade_id: "",
    section_id: "",
    subject_id: "",
    teacher_id: "",
    academic_year_id: ""
  });
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewTab, setViewTab] = useState("assignments"); // "assignments" | "tutors" | "orphans"
  const [orphans, setOrphans] = useState([]);
  const [tutorRows, setTutorRows] = useState([]); // assigned tutors (read-only view)
  const [showDeleteOrphans, setShowDeleteOrphans] = useState(false);
  const [deletingOrphans, setDeletingOrphans] = useState(false);
  // Bulk selection (checkboxes) for mass-deleting assignments
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const subdomain = user?.subdomain;
  // Orphan cleanup is a SUPPORT-only maintenance tool. Owners/admins must not see it.
  const isSupportSession = user?.is_support_session || user?.original_role === "system_admin_global";
  
  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`, { headers });
        setSettings(res.data);
      } catch (err) {
        console.error("Error loading settings:", err);
      }
    };
    loadSettings();
  }, []);
  
  // Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Build query params for assignments
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      // Fetch all data in parallel
      const [
        assignmentsRes,
        teachersSummaryRes,
        levelsRes,
        gradesRes,
        sectionsRes,
        subjectsRes,
        teachersRes,
        yearsRes
      ] = await Promise.all([
        axios.get(`${API}/academic/assignments?${params}`, { headers }),
        axios.get(`${API}/academic/assignments/teachers-summary`, { headers }),
        axios.get(`${API}/academic/levels`, { headers }),
        axios.get(`${API}/academic/grades`, { headers }),
        axios.get(`${API}/academic/sections`, { headers }),
        axios.get(`${API}/academic/subjects`, { headers }),
        axios.get(`${API}/users/teachers/active`, { headers }),
        axios.get(`${API}/academic/years`, { headers })
      ]);
      
      setAssignments(assignmentsRes.data);
      setTeachersSummary(teachersSummaryRes.data);
      // Orphan assignments (no valid linked course) — SUPPORT-only tool.
      // Loaded separately so a failure here never blocks the main list.
      if (isSupportSession) {
        try {
          const orphansRes = await axios.get(`${API}/academic/assignments/orphans`, { headers });
          setOrphans(orphansRes.data || []);
        } catch (e) {
          console.error("Error loading orphan assignments:", e);
        }
      }
      // Assigned tutors (read-only) for the "Tutores" tab — reuses the tutoring
      // overview. Non-blocking: a failure here never breaks the main list.
      try {
        const overviewRes = await axios.get(`${API}/admin/tutoring-overview`, { headers });
        const rows = (overviewRes.data?.rows || []).filter(r => r.tutor_id);
        setTutorRows(rows);
      } catch (e) {
        console.error("Error loading tutors overview:", e);
      }
      setAcademicData({
        levels: levelsRes.data,
        grades: gradesRes.data,
        sections: sectionsRes.data,
        subjects: subjectsRes.data,
        teachers: teachersRes.data,
        academicYears: yearsRes.data
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [filters]);
  
  const handleEdit = (assignment) => {
    setEditingAssignment(assignment);
    setShowModal(true);
  };
  
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await axios.delete(`${API}/academic/assignments/${deleteConfirm.id}`, { headers });
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting assignment:", error);
    }
  };

  // ── Bulk selection helpers ──────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const allVisibleSelected = assignments.length > 0 && assignments.every(a => selectedIds.includes(a.id));
  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(assignments.map(a => a.id));
      setSelectedIds(prev => prev.filter(id => !visibleIds.has(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...assignments.map(a => a.id)])));
    }
  };
  const clearSelection = () => setSelectedIds([]);

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API}/academic/assignments/bulk-delete`, { ids: selectedIds }, { headers });
      toast.success(res.data?.message || "Asignaciones eliminadas");
      setShowBulkDelete(false);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar las asignaciones");
    } finally {
      setBulkDeleting(false);
    }
  };

  // Clear selection whenever the filters change (the visible set changes)
  useEffect(() => { setSelectedIds([]); }, [filters]);

  const handleDeleteAllOrphans = async () => {
    setDeletingOrphans(true);
    try {
      const res = await axios.delete(`${API}/academic/assignments/orphans`, { headers });
      setShowDeleteOrphans(false);
      toast.success(res.data?.message || "Asignaciones huérfanas eliminadas");
      setViewTab("assignments");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar las huérfanas");
    } finally {
      setDeletingOrphans(false);
    }
  };
  
  const handleTeacherSelect = (teacherId) => {
    setFilters(prev => ({
      ...prev,
      teacher_id: teacherId || ""
    }));
  };
  
  const clearFilters = () => {
    setFilters({
      level_id: "",
      grade_id: "",
      section_id: "",
      subject_id: "",
      teacher_id: "",
      academic_year_id: ""
    });
  };
  
  if (loading) {
    return (
      <CourseLoadingScreen 
        title="Cargando asignaciones"
        subtitle="Preparando la gestión docente"
        icon={BookMarked}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex">
      <Sidebar 
        active="asignacion-docente"
        onNavigate={() => {}}
        expanded={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onLogout={onLogout}
        schoolName={settings?.system_name}
        subdomain={subdomain}
        user={user}
      />
      
      <div className="flex-1 flex flex-col min-w-0">
        <DashboardHeader 
          user={user}
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          onLogout={onLogout}
          logoUrl={settings?.logo_url}
          schoolName={settings?.system_name}
          subdomain={subdomain}
        />
        
        {/* Main Content */}
        <main className="flex-1 p-3 sm:p-4 lg:p-8 pb-20 lg:pb-8 overflow-x-hidden">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                    <BookMarked className="w-6 h-6 text-white" />
                  </div>
                  Asignación Docente
                </h1>
                <p className="text-gray-500 mt-1 ml-15">
                  Gestiona la asignación de profesores a asignaturas
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowBulkModal(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white rounded-xl hover:from-violet-600 hover:to-fuchsia-700 transition-all shadow-lg shadow-violet-500/25 font-medium"
                  data-testid="bulk-assignment-btn"
                >
                  <Users className="w-5 h-5" />
                  <span className="hidden sm:inline">Asignación Masiva</span>
                </button>
                <button
                  onClick={() => { setEditingAssignment(null); setShowModal(true); }}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all shadow-lg shadow-blue-500/25 font-medium"
                  data-testid="new-assignment-btn"
                >
                  <Plus className="w-5 h-5" />
                  <span className="hidden sm:inline">Nueva Asignación</span>
                </button>
              </div>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Left: Assignments List */}
            <div className="lg:col-span-3">
              {/* Teacher autocomplete search — filters the list to the selected teacher's assignments */}
              <TeacherSearchBar
                teachers={academicData.teachers}
                selectedId={filters.teacher_id}
                onSelect={(id) => setFilters(prev => ({ ...prev, teacher_id: id }))}
                onClear={() => setFilters(prev => ({ ...prev, teacher_id: "" }))}
                resultCount={assignments.length}
              />
              {/* Tabs: Asignaciones / Tutores / Huérfanas (Huérfanas solo para Soporte) */}
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <button
                  onClick={() => setViewTab("assignments")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${viewTab === "assignments" ? "bg-blue-600 text-white shadow-md shadow-blue-500/25" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}
                  data-testid="tab-assignments"
                >
                  <BookMarked className="w-4 h-4" />
                  Asignaciones
                </button>
                <button
                  onClick={() => setViewTab("tutors")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${viewTab === "tutors" ? "bg-emerald-600 text-white shadow-md shadow-emerald-500/25" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}
                  data-testid="tab-tutors"
                >
                  <UserCheck className="w-4 h-4" />
                  Tutores
                  {tutorRows.length > 0 && (
                    <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${viewTab === "tutors" ? "bg-white/25 text-white" : "bg-emerald-100 text-emerald-600"}`} data-testid="tutors-count-badge">
                      {tutorRows.length}
                    </span>
                  )}
                </button>
                {isSupportSession && (
                  <button
                    onClick={() => setViewTab("orphans")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${viewTab === "orphans" ? "bg-red-600 text-white shadow-md shadow-red-500/25" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}
                    data-testid="tab-orphans"
                  >
                    <Unlink className="w-4 h-4" />
                    Huérfanas
                    {orphans.length > 0 && (
                      <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${viewTab === "orphans" ? "bg-white/25 text-white" : "bg-red-100 text-red-600"}`} data-testid="orphans-count-badge">
                        {orphans.length}
                      </span>
                    )}
                  </button>
                )}
                {isSupportSession && <span className="ml-1 text-[11px] text-gray-400 italic hidden sm:inline">Huérfanas: herramienta de soporte</span>}
              </div>

              {viewTab === "tutors" ? (
                /* ───── Tutores view (read-only) ───── */
                <div data-testid="tutors-view">
                  {tutorRows.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mx-auto mb-4">
                        <UserCheck className="w-10 h-10 text-emerald-500" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Aún no hay tutores asignados</h3>
                      <p className="text-gray-500">Asigna tutores a las secciones desde <span className="font-medium">Gestión de Tutorías</span>.</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                      <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
                        <UserCheck className="w-4 h-4 text-emerald-600" />
                        <p className="text-sm font-semibold text-emerald-800">{tutorRows.length} tutor{tutorRows.length !== 1 ? "es" : ""} asignado{tutorRows.length !== 1 ? "s" : ""}</p>
                        <span className="text-[11px] text-emerald-600/80 hidden sm:inline">— gestiónalos en "Gestión de Tutorías"</span>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {tutorRows.map((r) => (
                          <div key={r.section_id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors" data-testid={`tutor-row-${r.section_id}`}>
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-sm font-bold shrink-0">
                              {(r.tutor_name || "?").trim().charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate" data-testid={`tutor-name-${r.section_id}`}>{r.tutor_name}</p>
                              <p className="text-xs text-gray-500 truncate">Tutor</p>
                            </div>
                            <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-600">
                              <GraduationCap className="w-3.5 h-3.5 text-blue-500" />
                              <span>{r.level_name || "—"}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-gray-700 font-medium">
                              <Layers className="w-3.5 h-3.5 text-purple-500" />
                              <span>{r.grade_name || "—"} {r.section_name ? `· ${r.section_name}` : ""}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 shrink-0 w-16 justify-end">
                              <Users className="w-3.5 h-3.5 text-emerald-500" />
                              <span>{r.student_count ?? 0}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : isSupportSession && viewTab === "orphans" ? (
                /* ───── Orphans view ───── */
                <div data-testid="orphans-view">
                  {orphans.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mx-auto mb-4">
                        <Check className="w-10 h-10 text-emerald-500" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay asignaciones huérfanas</h3>
                      <p className="text-gray-500">Todas las asignaciones tienen un curso vinculado. ¡Todo en orden!</p>
                    </div>
                  ) : (
                    <>
                      <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-red-800">
                              {orphans.length} asignación{orphans.length !== 1 ? "es" : ""} sin curso vinculado
                            </p>
                            <p className="text-xs text-red-600 mt-0.5">
                              Estas asignaciones quedaron sin un curso (asignatura) válido. Puedes eliminarlas todas de una vez.
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowDeleteOrphans(true)}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-medium shadow-lg shadow-red-500/25 shrink-0"
                          data-testid="delete-all-orphans-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                          Eliminar todas
                        </button>
                      </div>
                      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {orphans.map(assignment => (
                          <AssignmentCard 
                            key={assignment.id}
                            assignment={assignment}
                            onEdit={handleEdit}
                            onDelete={setDeleteConfirm}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                /* ───── Assignments view ───── */
                <>
              {/* Filters */}
              <FilterBar 
                filters={filters}
                setFilters={setFilters}
                levels={academicData.levels}
                grades={academicData.grades}
                sections={academicData.sections}
                subjects={academicData.subjects}
                teachers={academicData.teachers}
                academicYears={academicData.academicYears}
                onClear={clearFilters}
              />
            
            {/* Assignments Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
              </div>
            ) : assignments.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mx-auto mb-4">
                  <UserCheck className="w-10 h-10 text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {Object.values(filters).some(v => v) 
                    ? "No se encontraron asignaciones con los filtros seleccionados"
                    : "No hay asignaciones docentes"
                  }
                </h3>
                <p className="text-gray-500 mb-6">
                  {Object.values(filters).some(v => v)
                    ? "Prueba modificando los filtros o limpiándolos"
                    : "Comienza asignando profesores a las asignaturas"
                  }
                </p>
                {!Object.values(filters).some(v => v) && (
                  <button
                    onClick={() => { setEditingAssignment(null); setShowModal(true); }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all"
                  >
                    <Plus className="w-5 h-5" />
                    Nueva Asignación
                  </button>
                )}
              </div>
            ) : (
              <>
              {/* Bulk selection action bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4 px-4 py-3 bg-white rounded-xl border border-gray-100">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer" data-testid="assignment-select-all">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-400 cursor-pointer"
                  />
                  Seleccionar todas {selectedIds.length > 0 && <span className="text-blue-600">({selectedIds.length} seleccionada{selectedIds.length !== 1 ? "s" : ""})</span>}
                </label>
                {selectedIds.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={clearSelection}
                      className="px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      data-testid="assignment-clear-selection"
                    >
                      Limpiar
                    </button>
                    <button
                      onClick={() => setShowBulkDelete(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                      data-testid="assignment-bulk-delete-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                      Eliminar ({selectedIds.length})
                    </button>
                  </div>
                )}
              </div>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                {assignments.map(assignment => (
                  <AssignmentCard 
                    key={assignment.id}
                    assignment={assignment}
                    onEdit={handleEdit}
                    onDelete={setDeleteConfirm}
                    selected={selectedIds.includes(assignment.id)}
                    onToggleSelect={toggleSelect}
                  />
                ))}
              </div>
              </>
            )}
            
            {/* Results Count */}
            {!loading && assignments.length > 0 && (
              <div className="mt-4 text-sm text-gray-500 text-center">
                Mostrando {assignments.length} asignación{assignments.length !== 1 ? "es" : ""}
              </div>
            )}
                </>
              )}
          </div>
          
          {/* Right: Teachers Summary */}
          <div className="lg:col-span-1">
            <TeachersSummary 
              teachers={teachersSummary}
              onSelect={handleTeacherSelect}
              selectedTeacherId={filters.teacher_id}
            />
          </div>
        </div>
        </main>
      </div>
      
      {/* Modals */}
      <AssignmentModal 
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingAssignment(null); }}
        token={token}
        assignment={editingAssignment}
        onSuccess={fetchData}
        academicData={academicData}
        existingAssignments={assignments}
      />
      
      <BulkAssignmentModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        token={token}
        onSuccess={fetchData}
        academicData={academicData}
      />
      
      <ConfirmModal 
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title="Eliminar Asignación"
        message={`¿Estás seguro de eliminar esta asignación? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        confirmColor="red"
      />
      <ConfirmModal 
        isOpen={showDeleteOrphans}
        onClose={() => setShowDeleteOrphans(false)}
        onConfirm={handleDeleteAllOrphans}
        title="Eliminar asignaciones huérfanas"
        message={`Se eliminarán ${orphans.length} asignación(es) sin curso vinculado. Esta acción no se puede deshacer.`}
        confirmText={deletingOrphans ? "Eliminando..." : "Eliminar todas"}
        confirmColor="red"
      />
      <ConfirmModal 
        isOpen={showBulkDelete}
        onClose={() => setShowBulkDelete(false)}
        onConfirm={handleBulkDelete}
        title="Eliminar asignaciones seleccionadas"
        message={`Se eliminarán ${selectedIds.length} asignación(es) docente. El curso permanece; solo se quita el vínculo del profesor. Esta acción no se puede deshacer.`}
        confirmText={bulkDeleting ? "Eliminando..." : `Eliminar ${selectedIds.length}`}
        confirmColor="red"
      />
      <MobileBottomNav role={user?.role === "admin" ? "admin" : "owner"} />
      <FloatingHelpAvatar subdomain={user?.subdomain} />
    </div>
  );
}
